// iter_302 · 書類読み取り学習 — 共通部品（表示専用の小さな部品だけを置く）
import { useState } from 'react';
import { Info, CircleNotch } from '@phosphor-icons/react';

export const API = process.env.REACT_APP_BACKEND_URL;
export const LEARN = `${API}/api/doc-ai/learning`;
export const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
export const detailOf = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

export const STATE_JA = {
  candidate: '候補', confirmed: '確認済み', corrected: '修正済み',
  unknown: '不明', unreadable: '判読不能', absent: '記載なし', stale: '旧版',
};
export const ROLE_JA = {
  drawn_by: '製図者', designed_by: '設計者', checked_by: '検図者', approved_by: '承認者',
};
export const ENTITY_JA = {
  person: '個人名', department: '部署名', company: '会社名',
  role_mark: '処理印', unknown: '不明',
};

export const Panel = ({ title, hint, children, testId, action }) => (
  <section className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testId}>
    {(title || action) && (
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          {title && <h2 className="text-base font-semibold text-zinc-900">{title}</h2>}
          {hint && <p className="pt-0.5 text-[11.5px] leading-relaxed text-zinc-500">{hint}</p>}
        </div>
        {action}
      </div>
    )}
    <div className={title ? 'pt-3' : ''}>{children}</div>
  </section>
);

export const Pill = ({ children, tone = 'zinc', testId }) => (
  <span data-testid={testId}
    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-medium ${
      tone === 'teal' ? 'border-teal-200 bg-teal-50 text-teal-800'
        : tone === 'amber' ? 'border-amber-200 bg-amber-50 text-amber-900'
          : tone === 'rose' ? 'border-rose-200 bg-rose-50 text-rose-800'
            : 'border-zinc-200 bg-zinc-50 text-zinc-600'}`}>
    {children}
  </span>
);

export const Empty = ({ children, action, testId }) => (
  <div className="rounded-md border border-dashed border-zinc-200 px-4 py-6 text-center"
    data-testid={testId}>
    <p className="whitespace-pre-line text-[12px] leading-relaxed text-zinc-500">{children}</p>
    {action && <div className="pt-3">{action}</div>}
  </div>
);

// 用語の説明（ホバーではなく、押して開く。読み上げにも対応）
export const Help = ({ label, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="inline-flex flex-col">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="inline-flex min-h-[32px] items-center gap-1 text-[11.5px] text-teal-700 hover:underline"
        data-testid={testId}>
        <Info size={13} />{label}とは
      </button>
      {open && (
        <span className="mt-1 max-w-md rounded-md border border-teal-100 bg-teal-50/60 px-3 py-2 text-[11.5px] leading-relaxed text-zinc-700"
          data-testid={testId ? `${testId}-text` : undefined}>{children}</span>
      )}
    </span>
  );
};

export const Steps = ({ step, labels, testId }) => (
  <ol className="flex flex-wrap items-center gap-2" data-testid={testId}>
    {labels.map((l, i) => (
      <li key={l} className="flex items-center gap-2">
        <span className={`inline-flex min-h-[32px] items-center gap-2 rounded-full border px-3 text-[12px] ${
          i + 1 === step ? 'border-teal-700 bg-teal-700 text-white'
            : i + 1 < step ? 'border-teal-200 bg-teal-50 text-teal-800'
              : 'border-zinc-200 bg-white text-zinc-500'}`}>
          <span className="tabular-nums">{i + 1}</span>{l}
        </span>
        {i < labels.length - 1 && <span className="text-zinc-300">→</span>}
      </li>
    ))}
  </ol>
);

export const Collapse = ({ label, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-zinc-200" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className="flex min-h-[36px] w-full items-center justify-between px-3 text-[12px] text-zinc-700"
        data-testid={testId ? `${testId}-toggle` : undefined}>
        {label}<span className="text-zinc-400">{open ? '閉じる' : '開く'}</span>
      </button>
      {open && <div className="border-t border-zinc-200 px-3 py-2">{children}</div>}
    </div>
  );
};

export const Spinner = () => <CircleNotch size={16} className="animate-spin text-zinc-400" />;
